import { useEffect, useState } from 'react';
import { useChatBalancesConfig } from '../../api/hooks';
import { useSaveChatBalancesConfig } from '../../api/mutations';
import { btnPrimary, fieldLabelCls, inputCls } from '../../lib/styles';
import { Modal } from '../Modal';

/**
 * Настройка источника реальных остатков по чатам (URL + Bearer-токен внешнего
 * API + вкл/выкл). Токен наружу не отдаётся — показываем только факт «задан»;
 * пустое поле токена при сохранении = не менять. Доступно admin
 * (chat-balances.manage). Бэкенд проксирует API вживую (mixed-content-обход).
 */
export function ChatBalancesConfigModal({ onClose }: { onClose: () => void }) {
  const { data: config, isLoading } = useChatBalancesConfig();
  const save = useSaveChatBalancesConfig();
  const [enabled, setEnabled] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [transactionsApiUrl, setTransactionsApiUrl] = useState('');
  const [archiveApiUrl, setArchiveApiUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setApiUrl(config.apiUrl ?? '');
      setTransactionsApiUrl(config.transactionsApiUrl ?? '');
      setArchiveApiUrl(config.archiveApiUrl ?? '');
    }
  }, [config]);

  async function doSave() {
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        enabled,
        apiUrl: apiUrl.trim() || null,
        transactionsApiUrl: transactionsApiUrl.trim() || null,
        archiveApiUrl: archiveApiUrl.trim() || null,
        // Токен шлём только если оператор ввёл новый — иначе оставляем как есть.
        ...(apiToken.trim() ? { apiToken: apiToken.trim() } : {}),
      });
      setApiToken('');
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal title="Источник остатков по чатам" onClose={onClose} size="md">
      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-text2">
            Внешний read-only API с реальными остатками по каждому чату. Бэкенд
            проксирует его вживую на каждый запрос — данные всегда актуальные.
            Остатки сопоставляются с аккаунтами по Telegram chat id.
          </p>

          <label className="flex items-center gap-2 text-sm text-text">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Показывать остатки на странице «Балансы»
          </label>

          <div>
            <label className={fieldLabelCls}>URL источника</label>
            <input
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              placeholder="http://host:port/api/v1/chats/balances"
              className={`${inputCls} w-full font-mono`}
            />
          </div>

          <div>
            <label className={fieldLabelCls}>URL журнала движений (опционально)</label>
            <input
              value={transactionsApiUrl}
              onChange={(e) => setTransactionsApiUrl(e.target.value)}
              placeholder="http://host:port/api/v1/transactions"
              className={`${inputCls} w-full font-mono`}
            />
            <p className="mt-1 text-xs text-text2">
              Тот же токен. Включает пооперационный аудит в drill-down страницы «Аудит».
            </p>
          </div>

          <div>
            <label className={fieldLabelCls}>URL архива (опционально)</label>
            <input
              value={archiveApiUrl}
              onChange={(e) => setArchiveApiUrl(e.target.value)}
              placeholder="http://host:port/api/v1/archive/messages"
              className={`${inputCls} w-full font-mono`}
            />
            <p className="mt-1 text-xs text-text2">
              Тот же токен. Включает вкладку «Целостность» страницы «Аудит».
            </p>
          </div>

          <div>
            <label className={fieldLabelCls}>Токен (Bearer)</label>
            <input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder={config?.tokenSet ? '•••••• задан (пусто = не менять)' : 'токен не задан'}
              className={`${inputCls} w-full font-mono`}
            />
          </div>

          {error && <div className="text-xs text-danger">{error}</div>}
          {saved && <div className="text-xs text-success">✓ сохранено</div>}

          <div className="flex justify-end">
            <button onClick={() => void doSave()} disabled={save.isPending} className={btnPrimary}>
              {save.isPending ? '…' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
